import { useEffect, useState } from "react";

export const clamp01 = (v) => Math.max(0, Math.min(1, v));
const easeOut = (v) => 1 - Math.pow(1 - v, 3);
export const progress = (t, start, dur) => easeOut(clamp01((t - start) / dur));

// Gentle, endless drift — sinusoidal so the motion never resets
export const drift = (node, t, amp) => ({
  x: node.x + amp * node.amp * Math.sin(t * 0.42 + node.phase),
  y: node.y + amp * node.amp * Math.cos(t * 0.37 + node.phase * 1.3),
});

export const control = (p0, p1, curv) => {
  const dx = p1.x - p0.x, dy = p1.y - p0.y;
  return { x: (p0.x + p1.x) / 2 - dy * curv, y: (p0.y + p1.y) / 2 + dx * curv };
};

const f = (v) => v.toFixed(1);
export const quadPath = (p0, c, p1) => `M${f(p0.x)} ${f(p0.y)}Q${f(c.x)} ${f(c.y)} ${f(p1.x)} ${f(p1.y)}`;

export const quadPoint = (p0, c, p1, s) => {
  const u = 1 - s;
  return { x: u * u * p0.x + 2 * u * s * c.x + s * s * p1.x, y: u * u * p0.y + 2 * u * s * c.y + s * s * p1.y };
};

export const INTRO_END = 10;
export const SPARK_PERIOD = 4;
const TOUR_PERIOD = 5;

// After the map has grown, one theme at a time gently lights up its knowledge connections (endless, seamless cycle)
export const tourAt = (t, themeIds) => {
  if (t < INTRO_END || t > 999) return { id: null, env: 0 };
  const k = (t - INTRO_END) / TOUR_PERIOD;
  return { id: themeIds[Math.floor(k) % themeIds.length], env: Math.sin(Math.PI * (k % 1)) };
};

// Animation clock (~30 fps) that only advances while the graph is on screen and the tab is visible
export const useGraphClock = (ref, reduce) => {
  const [t, setT] = useState(reduce ? 1000 : 0);
  useEffect(() => {
    if (reduce) { setT(1000); return undefined; }
    const el = ref.current;
    let raf = 0, last = 0, elapsed = 0, visible = false;
    const loop = (now) => {
      if (visible && !document.hidden) {
        if (!last) last = now;
        if (now - last >= 33) {
          elapsed += Math.min(now - last, 100) / 1000;
          last = now;
          setT(elapsed);
        }
      } else {
        last = 0;
      }
      raf = requestAnimationFrame(loop);
    };
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0.1 });
    io.observe(el);
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [ref, reduce]);
  return t;
};

export const useNarrow = (query = "(max-width: 640px)") => {
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return narrow;
};

export const buildIndex = (graph) => {
  const nb = new Map();
  const add = (a, b, edge) => { if (!nb.has(a)) nb.set(a, []); nb.get(a).push({ id: b, edge }); };
  graph.edges.forEach((e) => { add(e.a, e.b, e.id); add(e.b, e.a, e.id); });
  return nb;
};

// Which nodes/edges to emphasise for a hovered or selected node
export const getFocus = (id, mode, graph, nb) => {
  if (!id) return null;
  const node = graph.byId[id];
  const nodes = new Set([id]);
  const edges = new Set();
  const links = (x) => nb.get(x) || [];
  if (mode === "select" && node.kind === "hub") {
    links(id).forEach(({ id: c, edge }) => {
      nodes.add(c); edges.add(edge);
      links(c).forEach((l) => { if (graph.byId[l.id].kind === "project") { nodes.add(l.id); edges.add(l.edge); } });
    });
  } else if (mode === "select" && node.kind === "project") {
    links(id).forEach(({ id: c, edge }) => { nodes.add(c); edges.add(edge); nodes.add(graph.byId[c].pathway); edges.add(`b:${c}`); });
  } else {
    links(id).forEach(({ id: x, edge }) => { nodes.add(x); edges.add(edge); });
  }
  return { nodes, edges };
};
